import { useOverviewQuery } from "../../store/mover/apis";
import { format, parseISO } from "date-fns";
import {
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableHeader,
  TableBody,
} from "@/components/ui/table";
import { FILTER_LABELS } from "@/constants";
import DateFilterLinks, { FilterKey } from "@/components/DateFilterLinks";
import { Link, useSearchParams } from "react-router-dom";
import { Loading } from "@/components/Loading";
import { Move } from "@/types";
import { Overview } from "@/store/mover/apis";

export default function Dashboard() {
  const [searchParams] = useSearchParams();
  const dateRangeFilter = searchParams.get("dateRangeFilter") ?? "today";
  const before = searchParams.get("before") ?? format(new Date(), "yyyy-MM-dd");
  const after = searchParams.get("after") ?? format(new Date(), "yyyy-MM-dd");

  const {
    data: overview,
    isLoading,
    isFetching,
  } = useOverviewQuery({
    after,
    before,
  });

  if (isLoading || isFetching) return <Loading />;
  if (!overview)
    return (
      <div className="p-6">
        <div className="flex flex-row justify-between items-end mb-6 pb-6 border-b border-1 border-stone-700">
          <div>
            <h1 className="text-2xl font-extrabold">Overview</h1>
            <h2 className="text-lg font-medium">
              {FILTER_LABELS[dateRangeFilter as keyof typeof FILTER_LABELS]} (
              {before === after
                ? format(parseISO(after), "MMM d, yyyy")
                : `${format(parseISO(after), "MMM d, yyyy")} - ${format(
                    parseISO(before),
                    "MMM d, yyyy"
                  )}`}
              )
            </h2>
          </div>
          <div>
            <DateFilterLinks defaultKey={dateRangeFilter as FilterKey} />
          </div>
        </div>
        <h2 className="text-lg font-semibold">No data available</h2>
      </div>
    );

  return (
    <div className="p-8">
      <div className="flex flex-row justify-between items-end mb-6 pb-6 border-b border-b-4 border-stone-300">
        <div>
          <h1 className="text-2xl font-extrabold">Overview</h1>
          <h2 className="text-lg font-medium">
            {FILTER_LABELS[dateRangeFilter as keyof typeof FILTER_LABELS]} (
            {before === after
              ? format(parseISO(after), "MMM d, yyyy")
              : `${format(parseISO(after), "MMM d, yyyy")} - ${format(
                  parseISO(before),
                  "MMM d, yyyy"
                )}`}
            )
          </h2>
        </div>
        <div>
          <DateFilterLinks defaultKey={dateRangeFilter as FilterKey} />
        </div>
      </div>

      <div
        id="metrics"
        className="grid grid-cols-2 lg:grid-cols-4 max-w-2xl gap-4 mb-6"
      >
        <div className="col-span-1">
          <div className="text-base text-stone-700 font-semibold">Jobs</div>
          <div className="text-2xl font-bold">{overview.jobs}</div>
          <div className="text-sm text-stone-700">
            Scheduled for this period
          </div>
        </div>

        <div className="col-span-1">
          <div className="text-base text-stone-700 font-semibold">Revenue</div>
          <div className="text-2xl font-bold">
            {Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(overview.revenue)}
          </div>
          <div className="text-sm text-stone-700">
            Projected from jobs in this period
          </div>
        </div>

        <div className="col-span-1">
          <div className="text-base text-stone-700 font-semibold">Payments</div>
          <div className="text-2xl font-bold">
            {Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(overview.payments)}
          </div>
          <div className="text-sm text-stone-700">
            Payments received in this period
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-stone-700">
        <div className="w-full lg:w-1/2 pb-0 lg:pb-0 lg:pr-6">
          <SalesPipeline overview={overview} />
        </div>

        <div className="w-full lg:w-1/2 pt-0 lg:pt-0 lg:pl-6">
          <LeadSources overview={overview} />
        </div>
      </div>
    </div>
  );
}

const SalesPipeline = ({ overview }: { overview: Overview }) => {
  const showList = (data: Move[], total: number) => {
    return (
      <>
        {data.map((lead) => (
          <Link
            target="_blank"
            className="text-sm font-medium"
            to={`/move/${lead.id}`}
            key={lead.id}
          >
            {lead.customer_name}
          </Link>
        ))}
        {total > 10 && (
          <div className="text-sm text-gray-800 font-medium">
            + {total > 10 ? total - 10 : 0} more
          </div>
        )}
      </>
    );
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Sales Pipeline</h3>
      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-1 text-sm text-stone-700 font-semibold underline underline-offset-4">
          Leads ({overview?.leads.total})
        </div>
        <div className="col-span-1 text-sm text-stone-700 font-semibold underline underline-offset-4">
          Estimated ({overview?.estimated.total})
        </div>
        <div className="col-span-1 text-sm text-stone-700 font-semibold underline underline-offset-4">
          Booked ({overview?.booked.total})
        </div>
        <div className="col-span-1 text-sm text-stone-700 font-semibold underline underline-offset-4">
          Lost ({overview?.lost.total})
        </div>

        <div className="col-span-1 flex flex-col gap-1">
          {showList(overview?.leads.data ?? [], overview?.leads.total ?? 0)}
        </div>

        <div className="col-span-1 flex flex-col gap-1">
          {showList(
            overview?.estimated.data ?? [],
            overview?.estimated.total ?? 0
          )}
        </div>

        <div className="col-span-1 flex flex-col gap-1">
          {showList(overview?.booked.data ?? [], overview?.booked.total ?? 0)}
        </div>

        <div className="col-span-1 flex flex-col gap-1">
          {showList(overview?.lost.data ?? [], overview?.lost.total ?? 0)}
        </div>
      </div>
    </div>
  );
};

const LeadSources = ({ overview }: { overview: Overview }) => {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Lead Sources</h3>
      <div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-0">Lead Source</TableHead>
              <TableHead>Leads</TableHead>
              <TableHead>Booked</TableHead>
              <TableHead>Revenue (projected)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {overview?.sources
              ?.filter((source: { leads: number }) => source.leads > 0)
              .map(
                (source: {
                  name: string;
                  leads: number;
                  booked: number;
                  revenue: number;
                }) => (
                  <TableRow key={source.name}>
                    <TableCell className="pl-0">{source.name}</TableCell>
                    <TableCell>{source.leads}</TableCell>
                    <TableCell>{source.booked}</TableCell>
                    <TableCell>
                      $
                      {source.revenue != null
                        ? new Intl.NumberFormat("en-US", {
                            maximumFractionDigits: 0,
                          }).format(Math.round(source.revenue))
                        : "0"}
                    </TableCell>
                  </TableRow>
                )
              )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
